import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Activity, TrendingUp, AlertCircle, ExternalLink, Loader2 } from 'lucide-react';

export default function AssessmentWidget() {
  const { user } = useAuth();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Show widget to all authenticated users
  if (!user) {
    return null;
  }

  useEffect(() => {
    fetchLatestAssessment();
  }, []);

  const fetchLatestAssessment = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const apiUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/assessment/latest`;
      
      const response = await fetch(apiUrl, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.hasAssessment) {
          setAssessment(data.assessment);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching assessment:', error);
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8.0) return 'text-green-600';
    if (score >= 6.0) return 'text-blue-600';
    if (score >= 4.0) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 8.0) return 'bg-green-50 border-green-200';
    if (score >= 6.0) return 'bg-blue-50 border-blue-200';
    if (score >= 4.0) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-600" />
            Pitch Readiness Assessment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!assessment) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-600" />
            Pitch Readiness Assessment
          </CardTitle>
          <CardDescription>
            Track your pitch readiness over time
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-4">
            You haven't completed a pitch readiness assessment yet.
          </p>
          
          <a
            href={`https://pitchready.impactacceleratorcircle.com?email=${encodeURIComponent(user.email)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Take Assessment
            <ExternalLink className="w-4 h-4" />
          </a>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      className={`cursor-pointer hover:shadow-lg transition-shadow border-2 ${getScoreBgColor(assessment.overall_score)}`}
      onClick={() => navigate('/assessment')}
    >
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-600" />
            <CardTitle>Pitch Readiness</CardTitle>
          </div>
          <span className="text-sm text-gray-500">
            Assessment #{assessment.assessment_number}
          </span>
        </div>
        <CardDescription>
          {new Date(assessment.completed_at).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className={`text-4xl font-bold ${getScoreColor(assessment.overall_score)}`}>
              {parseFloat(assessment.overall_score).toFixed(1)}
            </div>
            <div className="text-sm text-gray-500">out of 10</div>
          </div>

          <div className="text-right">
            <div className={`text-lg font-semibold ${getScoreColor(assessment.overall_score)}`}>
              {assessment.readiness_verdict}
            </div>
          </div>
        </div>

        {assessment.needsImprovement && assessment.needsImprovement.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
              <AlertCircle className="w-4 h-4" />
              <span className="font-medium">Focus Areas:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {assessment.needsImprovement.slice(0, 3).map((section, idx) => (
                <span 
                  key={idx}
                  className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded"
                >
                  {section.section_name}
                </span>
              ))}
            </div>
          </div>
        )}

        <Button 
          variant="ghost"
          className="mt-4 w-full flex items-center justify-center gap-2 text-blue-600 hover:text-blue-700"
          onClick={(e) => {
            e.stopPropagation();
            navigate('/assessment');
          }}
        >
          View Full Report
          <TrendingUp className="w-4 h-4" />
        </Button>
      </CardContent>
    </Card>
  );
}

